import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import LanguageDropdown from '../components/LanguageDropdown';

// Dynamically load reCAPTCHA script
const loadRecaptcha = (siteKey) => {
  return new Promise((resolve, reject) => {
    if (window.grecaptcha) {
      resolve(window.grecaptcha);
      return;
    }
    
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // Wait for grecaptcha to be ready
      window.grecaptcha.ready(() => resolve(window.grecaptcha));
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const Signup = () => {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  
  // Load reCAPTCHA on mount
  useEffect(() => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (siteKey) {
      loadRecaptcha(siteKey)
        .then(() => setRecaptchaLoaded(true))
        .catch(err => console.error('Failed to load reCAPTCHA:', err));
    }
    
    // Cleanup: hide badge when leaving the page
    return () => {
      const badge = document.querySelector('.grecaptcha-badge');
      if (badge) badge.style.visibility = 'hidden';
    };
  }, []);
  
  // Form data
  const [tenantName, setTenantName] = useState('');
  const [tenantPhone, setTenantPhone] = useState('');
  const [adminFullName, setAdminFullName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);

  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const getBackendUrl = () => {
    const envBackend = process.env.REACT_APP_BACKEND_URL;
    if (envBackend) {
      return envBackend;
    }
    // In production, nginx proxies /api to the backend on the same origin.
    // Only use :8001 in local dev (when running on port 3000).
    const origin = window.location.origin;
    if (window.location.port === '3000') {
      return origin.replace(':3000', ':8001');
    }
    return origin;
  };

  // Validation functions
  const validateField = (field, value, passwordToCompare = adminPassword) => {
    let error = '';
    
    switch(field) {
      case 'tenantName':
        if (!value.trim()) {
          error = t('validation.required', { field: t('auth.restaurantName') });
        } else if (value.trim().length < 2) {
          error = t('validation.minLength', { field: t('auth.restaurantName'), min: 2 });
        } else if (value.trim().length > 100) {
          error = t('validation.maxLength', { field: t('auth.restaurantName'), max: 100 });
        }
        break;
              case 'tenantPhone':
        if (!value.trim()) {
          error = t('validation.required', { field: t('auth.phone') });
        } else if (value.trim().length < 10) {
          error = t('validation.minLength', { field: t('auth.phone'), min: 10 });
        } else if (value.trim().length > 20) {
          error = t('validation.maxLength', { field: t('auth.phone'), max: 20 });
        } else if (!/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/i.test(value.replace(/\s/g, ''))) {
          error = t('validation.invalidPhone');
        }
        break;
              case 'adminFullName':
        if (!value.trim()) {
          error = t('validation.required', { field: t('auth.fullName') });
        } else if (value.trim().length < 3) {
          error = t('validation.minLength', { field: t('auth.fullName'), min: 3 });
        } else if (value.trim().length > 100) {
          error = t('validation.maxLength', { field: t('auth.fullName'), max: 100 });
        }
        break;
        
      case 'adminEmail':
        if (!value.trim()) {
          error = t('validation.required', { field: t('auth.email') });
        } else if (value.trim().length > 100) {
          error = t('validation.maxLength', { field: t('auth.email'), max: 100 });
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = t('validation.invalidEmail');
        }
        break;
        
      case 'adminPassword':
        if (!value) {
          error = t('validation.required', { field: t('auth.password') });
        } else if (value.length < 8) {
          error = t('validation.minLength', { field: t('auth.password'), min: 8 });
        } else if (value.length > 128) {
          error = t('validation.maxLength', { field: t('auth.password'), max: 128 });
        } else if (!/(?=.*[a-z])/.test(value)) {
          error = t('validation.passwordLowercase');
        } else if (!/(?=.*[A-Z])/.test(value)) {
          error = t('validation.passwordUppercase');
        } else if (!/(?=.*\d)/.test(value)) {
          error = t('validation.passwordDigit');
        }
        break;
        
      case 'confirmPassword':
        if (!value) {
          error = t('validation.required', { field: t('auth.passwordConfirm') });
        } else if (value !== passwordToCompare) {
          error = t('validation.passwordMismatch');
        }
        break;
        
      default:
        break;
    }
    
    return error;
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
    
    let value;
    switch(field) {
      case 'tenantName': value = tenantName; break;
      case 'tenantPhone': value = tenantPhone; break;
      case 'adminFullName': value = adminFullName; break;
      case 'adminEmail': value = adminEmail; break;
      case 'adminPassword': value = adminPassword; break;
      case 'confirmPassword': value = confirmPassword; break;
      default: value = '';
    }
    
    const error = validateField(field, value);
    setErrors({ ...errors, [field]: error });
  };

  const handleFieldChange = (field, value, setter) => {
    setter(value);
    
    // If password changes and confirm password was touched, re-validate confirmPassword
    if (field === 'adminPassword' && touched.confirmPassword && confirmPassword) {
      const confirmError = validateField('confirmPassword', confirmPassword, value);
      setErrors(prev => ({ ...prev, [field]: validateField(field, value), confirmPassword: confirmError }));
      return;
    }
    
    // Clear error when user starts typing
    if (touched[field] && errors[field]) {
      const newError = validateField(field, value);
      setErrors({ ...errors, [field]: newError });
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/(?=.*[a-z])/.test(password)) strength++;
    if (/(?=.*[A-Z])/.test(password)) strength++;
    if (/(?=.*\d)/.test(password)) strength++;
    if (/(?=.*[@$!%*?&])/.test(password)) strength++;
    
    if (strength <= 2) return { strength: 1, label: t('auth.passwordWeak'), color: 'bg-red-500' };
    if (strength <= 3) return { strength: 2, label: t('auth.passwordMedium'), color: 'bg-yellow-500' };
    return { strength: 3, label: t('auth.passwordStrong'), color: 'bg-green-500' };
  };

  const validateStep1 = () => {
    const nameError = validateField('tenantName', tenantName);
    const phoneError = validateField('tenantPhone', tenantPhone);
    
    setErrors({
      tenantName: nameError,
      tenantPhone: phoneError
    });
    
    setTouched({
      tenantName: true,
      tenantPhone: true
    });
    
    return !nameError && !phoneError;
  };

  const validateStep2 = () => {
    const nameError = validateField('adminFullName', adminFullName);
    const emailError = validateField('adminEmail', adminEmail);
    const passwordError = validateField('adminPassword', adminPassword);
    const confirmError = validateField('confirmPassword', confirmPassword);
    
    setErrors({
      adminFullName: nameError,
      adminEmail: emailError,
      adminPassword: passwordError,
      confirmPassword: confirmError
    });
    
    setTouched({
      adminFullName: true,
      adminEmail: true,
      adminPassword: true,
      confirmPassword: true
    });
    
    return !nameError && !emailError && !passwordError && !confirmError;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setStep(2);
      // Clear step 2 errors when moving forward
      setErrors({});
      setTouched({});
    }
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep2()) {
      return;
    }

    setLoading(true);

    try {
      // Execute reCAPTCHA v3 if configured
      const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
      let recaptchaToken = null;
      
      if (recaptchaSiteKey) {
        // Wait for grecaptcha to be ready with timeout
        const waitForRecaptcha = () => new Promise((resolve, reject) => {
          if (window.grecaptcha && window.grecaptcha.execute) {
            resolve(window.grecaptcha);
            return;
          }
          
          let attempts = 0;
          const maxAttempts = 50; // 5 seconds max wait
          const interval = setInterval(() => {
            attempts++;
            if (window.grecaptcha && window.grecaptcha.execute) {
              clearInterval(interval);
              resolve(window.grecaptcha);
            } else if (attempts >= maxAttempts) {
              clearInterval(interval);
              reject(new Error('reCAPTCHA loading timeout'));
            }
          }, 100);
        });
        
        try {
          const grecaptcha = await waitForRecaptcha();
          recaptchaToken = await grecaptcha.execute(recaptchaSiteKey, { action: 'signup' });
        } catch (recaptchaError) {
          console.error('reCAPTCHA error:', recaptchaError);
          // Don't block signup if reCAPTCHA fails - let backend decide
          console.warn('Proceeding without reCAPTCHA token');
        }
      }

      // Store language preference for verification page
      localStorage.setItem('signupLanguage', i18n.language);

      const backendUrl = getBackendUrl();
      // Auto-detect browser timezone for tenant default
      const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

      const response = await axios.post(
        `${backendUrl}/api/tenants/create`,
        {
          tenantName,
          tenantPhone,
          adminEmail,
          adminFullName,
          adminPassword,
          language: i18n.language,
          timezone: detectedTimezone,
          marketing_consent: marketingConsent,
          recaptcha_token: recaptchaToken,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data) {
        toast.success(t('messages.accountCreated'));
        navigate('/login');
      }
    } catch (error) {
      console.error('Signup error:', error);
      const errorMessage = error.response?.data?.detail || t('messages.signupError');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength(adminPassword);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-purple-700 text-white relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="pointer-events-none absolute -top-16 -left-16 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 right-0 w-72 h-72 bg-white/10 rounded-full blur-3xl" />

      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <a href="/">
            <img src="/idemenu-logo.svg" alt="idemenu" className="h-10 w-auto cursor-pointer" />
          </a>
          <LanguageDropdown variant="light" />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div className="space-y-8">
            <h1 className="text-4xl sm:text-5xl font-bold leading-tight">
              {t('auth.signupTitle')}
            </h1>
            <p className="text-lg text-purple-100 leading-relaxed">
              {t('auth.signupSubtitle')}
            </p>
            <div className="space-y-3 text-purple-100">
              <div className="flex items-start gap-3">
                <span className="mt-1">✓</span>
                <span>{t('auth.signupFeature1')}</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-1">✓</span>
                <span>{t('auth.signupFeature2')}</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-1">✓</span>
                <span>{t('auth.signupFeature3')}</span>
              </div>
            </div>
            <div className="text-sm text-purple-100">
              {t('auth.alreadyHaveAccount')} <Link to="/login" className="font-semibold text-white underline">{t('auth.login')}</Link>
            </div>
          </div>

          {/* Form card */}
          <div className="bg-white text-gray-900 rounded-2xl shadow-2xl p-8 sm:p-10 relative">
            <div className="absolute inset-0 rounded-2xl border border-white/60 pointer-events-none" />
            
            <div className="flex flex-col gap-2 mb-8">
              <div className="text-sm font-semibold text-purple-700">
                {t('auth.stepIndicator', { current: step, total: 2 })}
              </div>
              <h2 className="text-2xl font-bold">
                {step === 1 ? t('auth.step1Title') : t('auth.step2Title')}
              </h2>
              <p className="text-gray-600">
                {step === 1 ? t('auth.step1Description') : t('auth.step2Description')}
              </p>
            </div>

            {/* Progress Indicator */}
            <div className="flex gap-2 mb-8">
              <div className={`h-1 flex-1 rounded ${step >= 1 ? 'bg-purple-600' : 'bg-gray-200'}`}></div>
              <div className={`h-1 flex-1 rounded ${step >= 2 ? 'bg-purple-600' : 'bg-gray-200'}`}></div>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              {step === 1 ? (
                <>
                  {/* Step 1: Tenant Information */}
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      {t('auth.restaurantName')}
                    </label>
                    <input
                      type="text"
                      placeholder={t('auth.placeholders.restaurantName')}
                      value={tenantName}
                      onChange={(e) => handleFieldChange('tenantName', e.target.value, setTenantName)}
                      onBlur={() => handleBlur('tenantName')}
                      className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                        touched.tenantName && errors.tenantName ? 'border-red-500' : 'border-gray-200'
                      }`}
                    />
                    {touched.tenantName && errors.tenantName && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <span>⚠</span> {errors.tenantName}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      {t('auth.phone')}
                    </label>
                    <input
                      type="tel"
                      placeholder={t('auth.placeholders.phone')}
                      value={tenantPhone}
                      onChange={(e) => handleFieldChange('tenantPhone', e.target.value, setTenantPhone)}
                      onBlur={() => handleBlur('tenantPhone')}
                      className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                        touched.tenantPhone && errors.tenantPhone ? 'border-red-500' : 'border-gray-200'
                      }`}
                    />
                    {touched.tenantPhone && errors.tenantPhone && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <span>⚠</span> {errors.tenantPhone}
                      </p>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={handleNext}
                    className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg shadow-lg hover:from-purple-700 hover:to-indigo-700 transition-all"
                  >
                    {t('auth.continueButton')}
                  </button>
                </>
              ) : (
                <>
                  {/* Step 2: Admin Account */}
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      {t('auth.fullName')}
                    </label>
                    <input
                      type="text"
                      placeholder={t('auth.placeholders.fullName')}
                      value={adminFullName}
                      onChange={(e) => handleFieldChange('adminFullName', e.target.value, setAdminFullName)}
                      onBlur={() => handleBlur('adminFullName')}
                      className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                        touched.adminFullName && errors.adminFullName ? 'border-red-500' : 'border-gray-200'
                      }`}
                    />
                    {touched.adminFullName && errors.adminFullName && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <span>⚠</span> {errors.adminFullName}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      {t('auth.email')}
                    </label>
                    <input
                      type="email"
                      placeholder={t('auth.placeholders.email')}
                      value={adminEmail}
                      onChange={(e) => handleFieldChange('adminEmail', e.target.value, setAdminEmail)}
                      onBlur={() => handleBlur('adminEmail')}
                      className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                        touched.adminEmail && errors.adminEmail ? 'border-red-500' : 'border-gray-200'
                      }`}
                    />
                    {touched.adminEmail && errors.adminEmail && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <span>⚠</span> {errors.adminEmail}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      {t('auth.password')}
                    </label>
                    <input
                      type="password"
                      placeholder={t('auth.placeholders.password')}
                      value={adminPassword}
                      onChange={(e) => handleFieldChange('adminPassword', e.target.value, setAdminPassword)}
                      onBlur={() => handleBlur('adminPassword')}
                      className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                        touched.adminPassword && errors.adminPassword ? 'border-red-500' : 'border-gray-200'
                      }`}
                    />
                    {touched.adminPassword && errors.adminPassword && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <span>⚠</span> {errors.adminPassword}
                      </p>
                    )}
                    
                    {/* Password Strength Indicator */}
                    {adminPassword && (
                      <div className="mt-2">
                        <div className="flex gap-1 mb-1">
                          {[1, 2, 3].map((level) => (
                            <div
                              key={level}
                              className={`h-1 flex-1 rounded ${
                                level <= passwordStrength.strength
                                  ? passwordStrength.color
                                  : 'bg-gray-200'
                              }`}
                            ></div>
                          ))}
                        </div>
                        {passwordStrength.label && (
                          <p className="text-xs text-gray-600">
                            {t('auth.passwordStrength')}: {passwordStrength.label}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      {t('auth.passwordConfirm')}
                    </label>
                    <input
                      type="password"
                      placeholder={t('auth.placeholders.password')}
                      value={confirmPassword}
                      onChange={(e) => handleFieldChange('confirmPassword', e.target.value, setConfirmPassword)}
                      onBlur={() => handleBlur('confirmPassword')}
                      className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                        touched.confirmPassword && errors.confirmPassword ? 'border-red-500' : 'border-gray-200'
                      }`}
                    />
                    {touched.confirmPassword && errors.confirmPassword && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <span>⚠</span> {errors.confirmPassword}
                      </p>
                    )}
                  </div>

                  {/* Marketing consent (GDPR: opt-in, unchecked by default) */}
                  <div className="flex items-start gap-2 mt-1">
                    <input
                      type="checkbox"
                      id="marketing-consent"
                      checked={marketingConsent}
                      onChange={(e) => setMarketingConsent(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <label htmlFor="marketing-consent" className="text-xs text-gray-600 cursor-pointer leading-snug">
                      {t('auth.marketingConsent')}
                    </label>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="flex-1 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all"
                    >
                      {t('auth.backButton')}
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg shadow-lg hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-60"
                    >
                      {loading ? t('auth.creating') : t('auth.signup')}
                    </button>
                  </div>
                </>
              )}
            </form>

            <div className="mt-6 text-sm text-gray-600">
              {t('auth.alreadyHaveAccount')} <Link to="/login" className="font-semibold text-purple-700 hover:underline">{t('auth.login')}</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
